import { ApiError, normalizeSlug, passwordIssues, validateSlug } from '@fc/core'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Spinner,
} from '@fc/ui'
import { useMutation } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BrandLockup } from '../../components/brand'
import { signup, type SignupInput } from '../../lib/onboarding-api'

const GUESSED_TZ =
  typeof Intl !== 'undefined' ? (Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC') : 'UTC'

export function Signup() {
  const navigate = useNavigate()
  const [orgName, setOrgName] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [slug, setSlug] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Auto-derive the handle from the org name until the user edits it directly.
  const effectiveSlug = slugTouched ? normalizeSlug(slug) : normalizeSlug(orgName)
  const slugCheck = useMemo(() => validateSlug(effectiveSlug), [effectiveSlug])
  const pwIssues = useMemo(() => passwordIssues(password), [password])

  const mutation = useMutation({
    mutationFn: (input: SignupInput) => signup(input),
    onSuccess: () => navigate('/app'),
  })

  const conflict =
    mutation.isError && mutation.error instanceof ApiError && mutation.error.status === 409
  const fieldErrors =
    mutation.isError && mutation.error instanceof ApiError ? (mutation.error.fields ?? {}) : {}

  const canSubmit =
    orgName.trim().length >= 2 && slugCheck.ok && email.includes('@') && pwIssues.length === 0

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    mutation.mutate({
      orgName: orgName.trim(),
      slug: effectiveSlug,
      adminEmail: email.trim().toLowerCase(),
      password,
      timezone: GUESSED_TZ,
    })
  }

  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-6 py-12">
      <div
        className="absolute inset-x-0 top-0 -z-10 h-80 bg-brand-grad-soft blur-3xl"
        aria-hidden
      />
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <Link to="/" aria-label="FC ChMS home">
            <BrandLockup />
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Create your church workspace</CardTitle>
            <CardDescription>
              Set up your organization in under a minute. You'll be the owner.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="orgName">Church / organization name</Label>
                <Input
                  id="orgName"
                  required
                  autoFocus
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Grace Community Church"
                  aria-invalid={Boolean(fieldErrors.orgName) || undefined}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="slug">Workspace handle</Label>
                <div className="flex items-center rounded-md border border-border bg-bg-alt focus-within:border-secondary focus-within:ring-[3px] focus-within:ring-ring">
                  <span className="pl-3 text-sm text-dim">fcchms.app/</span>
                  <input
                    id="slug"
                    value={slugTouched ? slug : effectiveSlug}
                    onChange={(e) => {
                      setSlugTouched(true)
                      setSlug(e.target.value)
                    }}
                    placeholder="grace-community"
                    className="h-10 flex-1 bg-transparent px-1 text-sm text-fg placeholder:text-dim focus:outline-none"
                    aria-invalid={(!slugCheck.ok && effectiveSlug.length > 0) || undefined}
                    aria-describedby="slug-hint"
                  />
                </div>
                <p
                  id="slug-hint"
                  className={`text-xs ${!slugCheck.ok && effectiveSlug.length > 0 ? 'text-danger' : 'text-dim'}`}
                >
                  {effectiveSlug.length > 0 && !slugCheck.ok
                    ? slugCheck.reason
                    : 'Lowercase letters, numbers, and hyphens. This is your unique address.'}
                </p>
                {fieldErrors.slug && (
                  <p className="text-xs text-danger" role="alert">
                    {fieldErrors.slug}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">Your email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@church.org"
                  aria-invalid={Boolean(fieldErrors.adminEmail) || undefined}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  aria-describedby="pw-rules"
                />
                <ul id="pw-rules" className="space-y-0.5 pt-1 text-xs">
                  {[
                    { label: 'At least 10 characters', ok: password.length >= 10 },
                    {
                      label: 'An uppercase and lowercase letter',
                      ok: /[a-z]/.test(password) && /[A-Z]/.test(password),
                    },
                    { label: 'A number', ok: /[0-9]/.test(password) },
                  ].map((r) => (
                    <li
                      key={r.label}
                      className={`flex items-center gap-1.5 ${r.ok ? 'text-success' : 'text-dim'}`}
                    >
                      {r.ok ? <Check className="size-3.5" /> : <X className="size-3.5" />}
                      {r.label}
                    </li>
                  ))}
                </ul>
              </div>

              {conflict && (
                <p className="text-sm text-danger" role="alert">
                  That handle or email is already taken. Try a different one.
                </p>
              )}
              {mutation.isError && !conflict && Object.keys(fieldErrors).length === 0 && (
                <p className="text-sm text-danger" role="alert">
                  {mutation.error instanceof Error
                    ? mutation.error.message
                    : 'Something went wrong. Please try again.'}
                </p>
              )}

              <Button type="submit" className="w-full" disabled={mutation.isPending || !canSubmit}>
                {mutation.isPending ? <Spinner /> : null}
                Create workspace
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-sm text-muted">
          Already have a workspace?{' '}
          <Link to="/login" className="text-secondary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
